import { forwardRef, KeyboardEvent, useCallback } from 'react'
import { gql } from '@apollo/client'
import { COLOR } from '@npco/zeller-design-system'
import { useGetOutstandingExpensesFilter } from 'features/Cards/CardSingle/hooks/useGetOutstandingExpensesFilter/useGetOutstandingExpensesFilter'

import { DocumentNode } from 'graphql'
import { KEY_CODE } from 'const/keyboard'

import { AmountCell } from '../AmountCell/AmountCell'
import { BadgeAndNameCell } from '../BadgeAndNameCell/BadgeAndNameCell'
import { CategoryCell } from '../CategoryCell/CategoryCell'
import { ReceiptAndNoteCell } from '../ReceiptAndNoteCell/ReceiptAndNoteCell'
import { DebitCardTransaction } from '../TransactionDrawer'
import * as styled from './CorporateCardTransactionRow.styled'

interface CorporateCardTransactionRowProps {
  transaction: DebitCardTransaction
  showDetails: (transaction: DebitCardTransaction) => void
  isNew?: boolean
}

export const CorporateCardTransactionRow = forwardRef<
  HTMLLIElement,
  CorporateCardTransactionRowProps
>(({ transaction, showDetails, isNew }, ref) => {
  const { isOutstandingExpensesFilterOn } = useGetOutstandingExpensesFilter()
  const handleEnterKey = useCallback(
    (event: KeyboardEvent<HTMLLIElement>) => {
      if (event.key === KEY_CODE.ENTER) {
        showDetails(transaction)
      }
    },
    [transaction, showDetails]
  )

  return (
    <styled.Container
      data-testid="corporate-card-transaction-row"
      onClick={() => showDetails(transaction)}
      onKeyUp={handleEnterKey}
      tabIndex={0}
      $isNew={isNew}
      ref={ref}
    >
      <BadgeAndNameCell transaction={transaction} hideAvatarOnMobile={false} />
      <CategoryCell transaction={transaction} />
      <ReceiptAndNoteCell
        transaction={transaction}
        emptyIconColor={
          isOutstandingExpensesFilterOn ? COLOR.RED_400 : undefined
        }
      />
      <AmountCell transaction={transaction} />
    </styled.Container>
  )
}) as React.ForwardRefExoticComponent<
  CorporateCardTransactionRowProps & React.RefAttributes<HTMLLIElement>
> & { fragments: { DebitCardTransactionV2: DocumentNode } }

CorporateCardTransactionRow.fragments = {
  DebitCardTransactionV2: gql`
    fragment CorporateCardTransactionRowDebitCardTransactionV2Fragment on DebitCardTransactionV2 {
      ...BadgeAndNameCellDebitCardTransactionV2Fragment
      ...CategoryCellDebitCardTransactionV2Fragment
      ...ReceiptAndNoteCellDebitCardTransactionV2Fragment
      ...AmountCellDebitCardTransactionV2Fragment
    }

    ${BadgeAndNameCell.fragments.DebitCardTransactionV2}
    ${AmountCell.fragments.DebitCardTransactionV2}
  `,
}
